const GROUP_SELECTOR = [
  '.autoform-buttons',
  '.autoform-dialog-buttons',
  '.dialog-buttons',
  '.datagrid-wrap .dg-top-actions',
  '.ep-equal-button-group',
].join(', ');

const BUTTON_SELECTOR = ':scope > .ep-button';
const MEMBER_CLASS = 'ep-equal-width-member';
const MEASURING_CLASS = 'ep-button-group-measuring';
const OPT_OUT_SELECTOR = '[data-equal-width="false"], .ep-button-width-auto';
const AUTOFORM_GROUP_SELECTOR = '.autoform-buttons, .autoform-dialog-buttons';

const observedGroups = new WeakSet();
const pendingGroups = new Set();
let mutationObserver = null;
let resizeObserver = null;
let scheduledFrame = null;

const getLabel = (button) => {
  const explicit = button.querySelector?.('.button-text')?.textContent;
  return String(explicit ?? button.textContent ?? '').trim();
};

const isVisible = (button) => {
  if (button.hidden) return false;
  const style = globalThis.getComputedStyle?.(button);
  if (!style) return true;
  return style.display !== 'none' && style.visibility !== 'hidden';
};

const getMembers = (group) => Array.from(group.querySelectorAll(BUTTON_SELECTOR))
  .filter((button) => !button.matches(OPT_OUT_SELECTOR))
  .filter((button) => getLabel(button).length > 0)
  .filter(isVisible);

const clearMembers = (group) => {
  group.querySelectorAll(`:scope > .${MEMBER_CLASS}`)
    .forEach((button) => button.classList.remove(MEMBER_CLASS));
};

const clearGroupMetrics = (group) => {
  group.style.removeProperty('--ep-equal-button-width');
  group.style.removeProperty('--ep-equal-button-count');
  if (group.matches(AUTOFORM_GROUP_SELECTOR)) {
    group.style.removeProperty('grid-template-columns');
  }
};

export const equalizeButtonGroup = (group) => {
  if (!group?.isConnected) return 0;

  clearMembers(group);
  const buttons = getMembers(group);

  if (buttons.length < 2) {
    clearGroupMetrics(group);
    return 0;
  }

  buttons.forEach((button) => button.classList.add(MEMBER_CLASS));
  group.classList.add(MEASURING_CLASS);

  const width = Math.ceil(Math.max(
    0,
    ...buttons.map((button) => Math.max(
      button.getBoundingClientRect().width || 0,
      button.scrollWidth || 0,
    ))
  ));

  group.classList.remove(MEASURING_CLASS);

  if (width <= 0) {
    clearGroupMetrics(group);
    return 0;
  }

  const nextWidth = `${width}px`;
  if (group.style.getPropertyValue('--ep-equal-button-width') !== nextWidth) {
    group.style.setProperty('--ep-equal-button-width', nextWidth);
  }

  if (group.matches(AUTOFORM_GROUP_SELECTOR)) {
    group.style.setProperty('--ep-equal-button-count', String(buttons.length));
    group.style.setProperty(
      'grid-template-columns',
      `repeat(${buttons.length}, ${nextWidth})`
    );
  } else {
    group.style.removeProperty('--ep-equal-button-count');
  }

  return width;
};

const flushPendingGroups = () => {
  scheduledFrame = null;
  const groups = Array.from(pendingGroups);
  pendingGroups.clear();
  groups.forEach(equalizeButtonGroup);
};

const scheduleGroup = (group) => {
  if (!group?.isConnected) return;
  pendingGroups.add(group);
  if (scheduledFrame !== null) return;

  const schedule = globalThis.requestAnimationFrame
    || ((callback) => globalThis.setTimeout(callback, 0));
  scheduledFrame = schedule(flushPendingGroups);
};

const observeGroup = (group) => {
  if (!group) return;
  if (!observedGroups.has(group)) {
    observedGroups.add(group);
    resizeObserver?.observe(group);
  }
  scheduleGroup(group);
};

const scanNode = (node) => {
  const element = node?.nodeType === 1 ? node : node?.parentElement;
  if (!element) return;

  if (element.matches?.(GROUP_SELECTOR)) observeGroup(element);
  element.querySelectorAll?.(GROUP_SELECTOR).forEach(observeGroup);

  const parentGroup = element.closest?.(GROUP_SELECTOR);
  if (parentGroup) scheduleGroup(parentGroup);
};

const scheduleAllGroups = () => {
  document.querySelectorAll(GROUP_SELECTOR).forEach(observeGroup);
};

export const initEqualButtonGroups = () => {
  if (mutationObserver || typeof document === 'undefined') return;

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(({ target }) => {
        if (!target.classList.contains(MEASURING_CLASS)) scheduleGroup(target);
      });
    });
  }

  mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      const targetElement = record.target?.nodeType === 1
        ? record.target
        : record.target?.parentElement;
      const targetGroup = targetElement?.closest?.(GROUP_SELECTOR);
      if (targetGroup) scheduleGroup(targetGroup);

      record.addedNodes?.forEach(scanNode);
    });
  });

  const root = document.documentElement;
  if (root) {
    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  scheduleAllGroups();

  const fontsReady = document.fonts?.ready;
  if (fontsReady && typeof fontsReady.then === 'function') {
    fontsReady.then(scheduleAllGroups).catch(() => {});
  }
};

export default initEqualButtonGroups;
